import React, { useState, useEffect } from 'react';
import { Clock, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';

const BUCKETS = [
  { key: '0_60',   color: 'green',  bg: 'bg-green-50',   border: 'border-green-200',  badge: 'bg-green-100 text-green-800',  heading: 'bg-green-600' },
  { key: '61_90',  color: 'yellow', bg: 'bg-yellow-50',  border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-800', heading: 'bg-yellow-500' },
  { key: '91_180', color: 'orange', bg: 'bg-orange-50',  border: 'border-orange-200', badge: 'bg-orange-100 text-orange-800', heading: 'bg-orange-500' },
  { key: '180+',   color: 'red',    bg: 'bg-red-50',     border: 'border-red-200',    badge: 'bg-red-100 text-red-800',      heading: 'bg-red-600' }
];

const formatCurrency = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const ItemAging = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    fetch('/api/reports/item-aging', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.success) setData(d); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-red-600">Failed to load item aging report.</div>;
  }

  return (
    <div className="p-6 pb-12 bg-gray-50 min-h-screen">
      <PageIdentifier pageId="REP-007" pageName="Item Aging Report" />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Clock size={24} className="text-orange-600" />
          Item Aging Report
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          {data.totalItems} items currently In Stock — sorted by time since entry
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {BUCKETS.map(b => {
          const count = data.buckets[b.key]?.items.length ?? 0;
          return (
            <div key={b.key} className={`rounded-xl border p-4 ${b.bg} ${b.border}`}>
              <p className="text-sm font-medium text-gray-600">{data.buckets[b.key]?.label}</p>
              <p className={`text-3xl font-bold mt-1 text-${b.color}-700`}>{count}</p>
              <p className="text-xs text-gray-500 mt-1">items</p>
            </div>
          );
        })}
      </div>

      {/* Per-bucket detail tables */}
      <div className="space-y-4">
        {BUCKETS.map(b => {
          const bucket = data.buckets[b.key];
          const items = bucket?.items ?? [];
          const isOpen = expanded[b.key];

          return (
            <div key={b.key} className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
              <button
                className={`w-full flex items-center justify-between px-6 py-4 text-white ${b.heading} hover:opacity-90 transition-opacity`}
                onClick={() => toggle(b.key)}
              >
                <div className="flex items-center gap-3">
                  {b.key === '180+' && <AlertTriangle size={18} />}
                  <span className="font-semibold">{bucket?.label}</span>
                  <span className="bg-white bg-opacity-30 rounded-full px-2 py-0.5 text-xs font-bold">
                    {items.length}
                  </span>
                </div>
                {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>

              {isOpen && (
                <div className="overflow-x-auto">
                  {items.length === 0 ? (
                    <p className="px-6 py-4 text-sm text-gray-500">No items in this age range.</p>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {['Code', 'Description', 'Category', 'Vendor', 'Wt (g)', 'Purity', 'Sale Price', 'Days'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {items.map(item => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-mono font-semibold text-blue-700">{item.code}</td>
                            <td className="px-4 py-3 text-sm text-gray-800 max-w-xs truncate" title={item.description}>
                              {item.description || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.category_name || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.vendor_name || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {item.net_weight ? parseFloat(item.net_weight).toFixed(3) : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {item.gold_purity ? `${item.gold_purity}K` : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                              {formatCurrency(item.sale_price)}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${b.badge}`}>
                                {item.days_in_stock}d
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ItemAging;
